/**
 * 管理者API
 *
 * TASK-FE-301: 管理者ダッシュボード実装
 */

import apiClient from './client.js'

/**
 * 管理者認証API
 */
export const adminAuthApi = {
  /**
   * 管理者専用OTP要求
   * @param {string} email
   * @returns {Promise}
   */
  requestOtp(email) {
    return apiClient.post('/console/v1/admin/auth/request-otp', { email })
  }
}

/**
 * ユーザー管理API
 */
export const userManagementApi = {
  /**
   * ユーザー一覧取得
   * @param {Object} params - { account_type, limit }
   * @returns {Promise}
   */
  listUsers(params = {}) {
    return apiClient.get('/console/v1/admin/users', { params })
  },

  /**
   * ユーザー詳細取得
   * @param {string} userId
   * @returns {Promise}
   */
  getUser(userId) {
    return apiClient.get(`/console/v1/admin/users/${userId}`)
  },

  /**
   * アカウント種別変更
   * @param {string} userId
   * @param {string} accountType - 'trial' | 'individual' | 'corporate' | 'data_provider'
   * @returns {Promise}
   */
  changeAccountType(userId, accountType) {
    return apiClient.post(`/console/v1/admin/users/${userId}/change-type`, {
      account_type: accountType
    })
  },

  /**
   * ユーザー停止
   * @param {string} userId
   * @returns {Promise}
   */
  suspendUser(userId) {
    return apiClient.post(`/console/v1/admin/users/${userId}/suspend`)
  },

  /**
   * ユーザー再開
   * @param {string} userId
   * @returns {Promise}
   */
  activateUser(userId) {
    return apiClient.post(`/console/v1/admin/users/${userId}/activate`)
  }
}

/**
 * 法人アカウント管理API
 */
export const corporateApi = {
  /**
   * 法人アカウント作成
   * @param {Object} data
   * @returns {Promise}
   */
  create(data) {
    return apiClient.post('/console/v1/admin/corporate/create', data)
  },

  /**
   * 法人アカウント一覧
   * @param {Object} params - { status, limit }
   * @returns {Promise}
   */
  list(params = {}) {
    return apiClient.get('/console/v1/admin/corporate', { params })
  },

  /**
   * 法人アカウント詳細
   * @param {string} corporateId
   * @returns {Promise}
   */
  get(corporateId) {
    return apiClient.get(`/console/v1/admin/corporate/${corporateId}`)
  },

  /**
   * 法人アカウント更新
   * @param {string} corporateId
   * @param {Object} data
   * @returns {Promise}
   */
  update(corporateId, data) {
    return apiClient.post(`/console/v1/admin/corporate/${corporateId}/update`, data)
  },

  /**
   * 法人アカウント停止
   * @param {string} corporateId
   * @returns {Promise}
   */
  suspend(corporateId) {
    return apiClient.post(`/console/v1/admin/corporate/${corporateId}/suspend`)
  },

  /**
   * 法人アカウント再開
   * @param {string} corporateId
   * @returns {Promise}
   */
  activate(corporateId) {
    return apiClient.post(`/console/v1/admin/corporate/${corporateId}/activate`)
  }
}

/**
 * 振込確認API
 */
export const transferApi = {
  /**
   * 振込待ち一覧
   * @param {Object} params - { limit }
   * @returns {Promise}
   */
  getPending(params = {}) {
    return apiClient.get('/console/v1/admin/transfers/pending', { params })
  },

  /**
   * 振込確認
   * @param {string} invoiceId
   * @param {Object} data - { payment_date, transfer_amount, notes }
   * @returns {Promise}
   */
  confirm(invoiceId, data) {
    return apiClient.post(`/console/v1/admin/transfers/${invoiceId}/confirm`, data)
  }
}

/**
 * 価格管理API
 */
export const pricingApi = {
  /**
   * 現在の価格一覧
   * @returns {Promise}
   */
  getCurrent() {
    return apiClient.get('/console/v1/admin/pricing/current')
  },

  /**
   * 価格履歴
   * @param {Object} params - { rule_type, resource_name }
   * @returns {Promise}
   */
  getHistory(params = {}) {
    return apiClient.get('/console/v1/admin/pricing/history', { params })
  },

  /**
   * 特定日の価格
   * @param {Object} params - { date, rule_type, resource_name }
   * @returns {Promise}
   */
  getAtDate(params) {
    return apiClient.get('/console/v1/admin/pricing/at-date', { params })
  },

  /**
   * 新規価格ルール作成
   * @param {Object} data
   * @returns {Promise}
   */
  create(data) {
    return apiClient.post('/console/v1/admin/pricing/create', data)
  },

  /**
   * 価格ルール更新
   * @param {number} ruleId
   * @param {Object} data - { description }
   * @returns {Promise}
   */
  update(ruleId, data) {
    return apiClient.post(`/console/v1/admin/pricing/update/${ruleId}`, data)
  }
}

/**
 * 収益分析API
 */
export const revenueApi = {
  /**
   * プラットフォームAPI収益取得（unit_price）
   * @param {Object} params - { year_month }
   * @returns {Promise}
   */
  getPlatformApiRevenue(params = {}) {
    return apiClient.get('/console/v1/admin/platform/revenue', { params })
  },

  /**
   * データプロバイダー別収益一覧
   * @param {Object} params - { year_month }
   * @returns {Promise}
   */
  getDataProviderList(params = {}) {
    return apiClient.get('/console/v1/admin/data-providers/revenue', { params })
  },

  /**
   * データプロバイダー別収益詳細
   * @param {string} userId - プロバイダーユーザーID
   * @param {Object} params - { year_month }
   * @returns {Promise}
   */
  getDataProviderDetail(userId, params = {}) {
    return apiClient.get(`/console/v1/admin/data-providers/${userId}/revenue-detail`, { params })
  }
}
